import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { attendanceErrorMessage, locationOutcomeLabel } from "@/lib/attendanceErrors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode } from "lucide-react";

const LOCATION_HINT =
  "Location is used only when you check in or out to verify this attendance action. There is no continuous tracking.";

const PERSON_TYPES = [
  { id: "employee", label: "Employee" },
  { id: "contractor", label: "Contractor" },
  { id: "visitor", label: "Visitor" },
];

function locationSecureContextOk() {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname || "";
  if (host === "localhost" || host === "127.0.0.1") return true;
  return Boolean(window.isSecureContext);
}

async function readBrowserLocation() {
  if (!navigator.geolocation) {
    return { location_status: "unavailable" };
  }
  // Browsers block GPS on plain http://LAN-IP — same denial on-site or overseas.
  if (!locationSecureContextOk()) {
    return { location_status: "permission_denied" };
  }
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      });
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy_m: pos.coords.accuracy,
      location_status: "ok",
    };
  } catch (err) {
    if (err && err.code === 1) return { location_status: "permission_denied" };
    return { location_status: "unavailable" };
  }
}

function Spinner({ label = "Loading…" }) {
  return (
    <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
      <div
        className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
        aria-hidden="true"
      />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function AttendanceScanPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, acceptSession } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyHint, setBusyHint] = useState("");
  const [error, setError] = useState(null);
  const [workerName, setWorkerName] = useState("");
  const [personType, setPersonType] = useState("employee");
  const [contractorId, setContractorId] = useState("");
  const [companyName, setCompanyName] = useState("");

  const scanPath = token && !token.includes("/") ? `/a/${token}` : null;

  const goToPassHome = useCallback(() => {
    navigate("/attendance/pass", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    if (!scanPath) {
      setState({
        state: "unavailable",
        message: "This attendance QR code is unavailable.",
        location_policy: LOCATION_HINT,
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/attendance/scan/${encodeURIComponent(token)}`);
      if (data.state === "ready_to_check_out" && data.pass) {
        goToPassHome();
        return;
      }
      setState(data);
      if (data.worker_name) setWorkerName(data.worker_name);
      if (data.person_type) setPersonType(data.person_type);
      const contractorsList = Array.isArray(data.contractors) ? data.contractors : [];
      const savedContractorId = data.contractor_id_saved || "";
      if (
        savedContractorId &&
        contractorsList.some((c) => c.id === savedContractorId)
      ) {
        setContractorId(savedContractorId);
        setCompanyName("");
      } else if (data.company_name_saved && data.person_type !== "employee") {
        setContractorId("other");
        setCompanyName(data.company_name_saved);
      } else {
        setContractorId("");
        setCompanyName("");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setState({
          state: "unavailable",
          message: "This attendance QR code is unavailable.",
          location_policy: LOCATION_HINT,
        });
      } else if (err.response?.status === 403) {
        setState({
          state: "unavailable",
          message: attendanceErrorMessage(
            err,
            "Your account is disabled. Contact your administrator.",
          ),
          location_policy: LOCATION_HINT,
        });
      } else {
        setError(attendanceErrorMessage(err, "Unable to load attendance. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  }, [scanPath, token, goToPassHome]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load, user?.id]);

  function buildPersonPayload() {
    const payload = {
      name: workerName.trim(),
      person_type: personType,
    };
    if (personType === "contractor") {
      if (contractorId && contractorId !== "other") {
        payload.contractor_id = contractorId;
      } else {
        payload.contractor_id = "other";
        payload.company_name = companyName.trim();
      }
    }
    if (personType === "visitor") {
      if (contractorId && contractorId !== "other") {
        payload.contractor_id = contractorId;
      } else {
        payload.company_name = companyName.trim();
      }
    }
    return payload;
  }

  async function runCheckIn() {
    setBusyHint("Requesting location… Allow GPS when prompted.");
    const location = await readBrowserLocation();
    setBusyHint("Checking in…");
    await api.post(`/attendance/scan/${encodeURIComponent(token)}/check-in`, location);
    goToPassHome();
  }

  async function joinAndCheckIn(e) {
    e.preventDefault();
    if (!scanPath || busy) return;
    if (!workerName.trim()) {
      setError("Enter your name to continue.");
      return;
    }
    if (personType === "contractor" && (!contractorId || contractorId === "other") && !companyName.trim()) {
      setError("Who are you working for? Select a company or enter Other.");
      return;
    }
    if (personType === "visitor" && (!contractorId || contractorId === "other") && !companyName.trim()) {
      setError("What company are you visiting from?");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = buildPersonPayload();
      if (state?.needs_person_profile && user) {
        setBusyHint("Saving your details…");
        const { data } = await api.post(
          `/attendance/scan/${encodeURIComponent(token)}/person`,
          payload,
        );
        setState(data.scan);
      } else {
        setBusyHint("Setting up your free attendance account…");
        const { data } = await api.post(
          `/attendance/scan/${encodeURIComponent(token)}/join`,
          payload,
        );
        await acceptSession(data.token, data.user);
        setState(data.scan);
      }
      await runCheckIn();
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setState({
          state: "unavailable",
          message: "This attendance QR code is unavailable.",
          location_policy: LOCATION_HINT,
        });
      } else if (status === 422) {
        setState((prev) => ({
          ...(prev || {}),
          state: "ready_to_check_in",
          action: "check_in",
          message: attendanceErrorMessage(err, "Check-in denied."),
        }));
        setError(null);
      } else {
        setError(attendanceErrorMessage(err, "Could not complete check-in."));
      }
    } finally {
      setBusy(false);
      setBusyHint("");
    }
  }

  async function runAction(kind) {
    if (!scanPath || busy) return;
    setBusy(true);
    setError(null);
    setBusyHint("Requesting location… Allow GPS when prompted.");
    try {
      const location = await readBrowserLocation();
      setBusyHint(kind === "check_in" ? "Checking in…" : "Checking out…");
      const path =
        kind === "check_in"
          ? `/attendance/scan/${encodeURIComponent(token)}/check-in`
          : `/attendance/scan/${encodeURIComponent(token)}/check-out`;
      const { data } = await api.post(path, location);
      if (kind === "check_in" || data.pass) {
        goToPassHome();
        return;
      }
      setState(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 422 && kind === "check_in") {
        setState((prev) => ({
          ...(prev || {}),
          state: "ready_to_check_in",
          action: "check_in",
          message: attendanceErrorMessage(err, "Check-in denied."),
        }));
        setError(null);
      } else if (status === 409) {
        setState((prev) => ({
          ...(prev || {}),
          state: "checked_in_elsewhere",
          action: null,
          message: attendanceErrorMessage(
            err,
            "You are already checked in at another site. Check out there first.",
          ),
        }));
      } else if (status === 404) {
        setState({
          state: "unavailable",
          message: "This attendance QR code is unavailable.",
          location_policy: LOCATION_HINT,
        });
      } else if (status === 401) {
        setState((prev) => ({
          ...(prev || {}),
          state: "authentication_required",
          action: "join",
          message: "Enter your name to check in at this site.",
        }));
      } else if (status === 400) {
        setState((prev) => ({
          ...(prev || {}),
          needs_person_profile: true,
          action: "person_profile",
          message: attendanceErrorMessage(err, "Confirm who you are to continue."),
        }));
      } else {
        setError(attendanceErrorMessage(err, "Attendance action failed. Please try again."));
      }
    } finally {
      setBusy(false);
      setBusyHint("");
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Spinner label="Loading attendance…" />
      </div>
    );
  }

  const view = state || {
    state: "unavailable",
    message: "This attendance QR code is unavailable.",
    location_policy: LOCATION_HINT,
  };

  const needsJoin =
    view.state === "authentication_required" || Boolean(view.needs_person_profile);
  const contractors = Array.isArray(view.contractors) ? view.contractors : [];
  const showOther =
    personType === "contractor" || personType === "visitor"
      ? !contractorId || contractorId === "other"
      : false;

  const primary =
    !needsJoin && view.action === "check_in"
      ? { label: "Check In", kind: "check_in" }
      : null;

  const outcomeLabel = locationOutcomeLabel(view.location_outcome);
  const checkInDenied =
    typeof view.message === "string" &&
    view.message.toLowerCase().startsWith("check-in denied");
  const insecureLocation =
    typeof window !== "undefined" && !locationSecureContextOk();

  return (
    <div
      className="min-h-screen bg-background flex flex-col px-4 py-8 safe-area-pb"
      data-testid="attendance-scan-page"
    >
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col justify-center gap-6">
        <div className="flex items-center gap-2 text-primary">
          <QrCode size={22} aria-hidden="true" />
          <span className="font-heading font-semibold text-sm tracking-wide">Smart Attendance</span>
        </div>

        <header className="space-y-2">
          {view.company_name && (
            <p className="text-sm text-muted-foreground" data-testid="attendance-scan-company">
              {view.company_name}
            </p>
          )}
          <h1 className="font-heading text-2xl font-bold" data-testid="attendance-scan-title">
            {view.site_name || "Site attendance"}
          </h1>
          <p
            className={
              checkInDenied
                ? "text-base font-medium text-destructive"
                : "text-base text-muted-foreground"
            }
            data-testid="attendance-scan-message"
            role="status"
            aria-live="polite"
          >
            {view.message}
          </p>
        </header>

        {insecureLocation && (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-sm text-destructive"
            data-testid="attendance-scan-insecure-location"
            role="alert"
          >
            This page is not secure (http), so the phone cannot share GPS — check-in
            will look the same on-site or far away. Open the HTTPS attendance link
            and allow Location when asked.
          </div>
        )}

        {needsJoin && (
          <form onSubmit={joinAndCheckIn} className="space-y-4" data-testid="attendance-scan-join">
            <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-3 space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Site</p>
              <p className="font-medium">
                {view.company_name ? `${view.company_name} · ` : ""}
                {view.site_name || "This site"}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="worker-name">Full name</Label>
              <Input
                id="worker-name"
                name="name"
                autoComplete="name"
                value={workerName}
                onChange={(e) => setWorkerName(e.target.value)}
                placeholder="e.g. Alex Rivera"
                required
                className="min-h-12 text-base"
                data-testid="attendance-scan-name"
              />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">I am a:</legend>
              <div className="grid grid-cols-3 gap-2" data-testid="attendance-scan-person-type">
                {PERSON_TYPES.map((pt) => (
                  <button
                    key={pt.id}
                    type="button"
                    className={`min-h-12 rounded-md border text-sm font-medium touch-manipulation ${
                      personType === pt.id
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground"
                    }`}
                    aria-pressed={personType === pt.id}
                    onClick={() => {
                      setPersonType(pt.id);
                      setContractorId("");
                      setCompanyName("");
                    }}
                    data-testid={`attendance-person-${pt.id}`}
                  >
                    {pt.label}
                  </button>
                ))}
              </div>
            </fieldset>

            {personType === "contractor" && (
              <div className="space-y-2" data-testid="attendance-contractor-fields">
                <Label htmlFor="contractor-select">Who are you working for?</Label>
                <select
                  id="contractor-select"
                  className="w-full min-h-12 rounded-md border border-input bg-background px-3 text-base"
                  value={contractorId}
                  onChange={(e) => setContractorId(e.target.value)}
                  data-testid="attendance-contractor-select"
                >
                  <option value="">Select contractor…</option>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
                {showOther && (
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company name"
                    className="min-h-12 text-base"
                    data-testid="attendance-contractor-other"
                  />
                )}
              </div>
            )}

            {personType === "visitor" && (
              <div className="space-y-2" data-testid="attendance-visitor-fields">
                <Label htmlFor="visitor-company">What company are you visiting from?</Label>
                {contractors.length > 0 && (
                  <select
                    id="visitor-company"
                    className="w-full min-h-12 rounded-md border border-input bg-background px-3 text-base"
                    value={contractorId}
                    onChange={(e) => setContractorId(e.target.value)}
                    data-testid="attendance-visitor-select"
                  >
                    <option value="">Select or enter below…</option>
                    {contractors.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                    <option value="other">Other / type below</option>
                  </select>
                )}
                {(contractors.length === 0 || showOther) && (
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Your company"
                    className="min-h-12 text-base"
                    data-testid="attendance-visitor-company"
                  />
                )}
              </div>
            )}

            <Button
              type="submit"
              className="w-full text-lg min-h-14 py-6 btn-glow touch-manipulation"
              disabled={busy}
              data-testid="attendance-scan-join-submit"
            >
              {busy ? "Working…" : "Continue"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Creates a free attendance account on this device — no email or phone required.
            </p>
            {!user && (
              <Link
                to="/login"
                state={{ from: scanPath }}
                className="block text-center text-sm text-muted-foreground hover:text-primary"
                data-testid="attendance-scan-signin"
              >
                Already have a Smart QR account? Sign in
              </Link>
            )}
          </form>
        )}

        {!needsJoin && (
          <p
            className="text-sm text-muted-foreground leading-relaxed rounded-md border border-border/60 bg-muted/30 px-3 py-3"
            data-testid="attendance-scan-location-policy"
          >
            {view.location_policy || LOCATION_HINT}
          </p>
        )}

        {outcomeLabel && (
          <p className="text-sm text-foreground/80" data-testid="attendance-scan-location-outcome">
            {outcomeLabel}
          </p>
        )}

        {error && (
          <div
            className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3"
            data-testid="attendance-scan-error"
            role="alert"
          >
            {error}
          </div>
        )}

        {busy && busyHint && (
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            {busyHint}
          </p>
        )}

        {primary && (
          <Button
            className="w-full text-lg min-h-14 py-6 btn-glow touch-manipulation"
            disabled={busy}
            data-testid={`attendance-scan-${primary.kind}`}
            aria-label={`${primary.label} at ${view.site_name || "this site"}`}
            onClick={() => runAction(primary.kind)}
          >
            {busy ? "Working…" : primary.label}
          </Button>
        )}

        {!primary && !needsJoin && view.state === "checked_in_elsewhere" && (
          <Button
            className="w-full min-h-12"
            onClick={goToPassHome}
            data-testid="attendance-go-to-pass"
          >
            Open my Daily Pass
          </Button>
        )}

        {!primary && !needsJoin && view.state !== "unavailable" && view.state !== "checked_in_elsewhere" && (
          <Button
            variant="outline"
            className="w-full min-h-12"
            disabled={busy}
            onClick={load}
            aria-label="Refresh attendance status"
          >
            Refresh status
          </Button>
        )}
      </div>
    </div>
  );
}
